import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { AutomaticPlanSelector } from '@yola/webapp-plugins';
import { selectors } from '@yola/subscription-manager-js';
import { i18next, dialogs } from '@yola/ws-sdk';
import yolaEditorDialogs from 'yola-editor/src/js/modules/dialogs';
import user from 'yola-editor/src/js/modules/user';
import getPartnerData from 'yola-editor/src/js/modules/user/selectors/partner-data';
import useDomainsExtraPrice from 'yola-editor/src/js/modules/upsell/hooks/use-domains-extra-price';
import getSiteId from 'yola-editor/src/js/modules/auth/selectors/site-id';
import getPurchaseFlowType from 'yola-editor/src/js/modules/upsell/helpers/get-purchase-flow-type';
import {
  CUSTOM_DOMAIN,
  POINTED_DOMAIN,
  BRANDED_HEADER,
  BRANDED_FOOTER,
} from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import { redirectToUrl } from 'yola-editor/src/js/router/utils/redirect-to-url';
import actionIds from 'yola-editor/src/js/modules/upsell/constants/domain-purchase-action-ids.js';
import publishing from 'yola-editor/src/js/modules/publishing';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import segment from 'src/js/modules/analytics/segment';
import getTemplateBuildSlugTrait from 'src/js/modules/analytics/segment/helpers/get-template-build-slug-trait';
import getIsB2C from 'src/js/modules/config/selectors/is-b2c';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';

const {
  constants,
  trackAsync,
  trackers: { trackEvent },
} = segment;
const {
  common: { EDITOR_PRODUCT_CATEGORY },
  events,
} = constants;

const PURCHASED_FLOW_ID = 'site_publish';
const getPremiumFeature = (actionId, isBrandedHeaderEnabled) => {
  switch (actionId) {
    case actionIds.BUY_DOMAIN:
      return CUSTOM_DOMAIN;
    case actionIds.THIRD_PARTY_DOMAIN:
      return POINTED_DOMAIN;
    case actionIds.GET_FREE_DOMAIN:
    default:
      return isBrandedHeaderEnabled ? BRANDED_HEADER : BRANDED_FOOTER;
  }
};

const AutomaticPlanSelectorContainer = ({
  actionId,
  domainName = '',
  successUrl = '',
  onCancel,
}) => {
  const siteId = useSelector(getSiteId);
  const isB2C = useSelector(getIsB2C);
  const availablePackages = useSelector(selectors.getHostingPackages);
  const partnerData = useSelector(getPartnerData);
  const { currency } = useSelector(user.selectors.getUserPreferences);
  const purchaseFlowType = useSelector(getPurchaseFlowType);

  const dispatch = useDispatch();

  const [
    { branded_header: isBrandedHeaderEnabled, gold_reinforcement: isGoldReinforcementEnabled },
  ] = useFeatureFlags(['branded_header', 'gold_reinforcement']);
  const discountType = useDomainsExtraPrice();
  const premiumFeature = getPremiumFeature(actionId, isBrandedHeaderEnabled);

  const traits = {
    purchaseFlowType,
    productCategory: EDITOR_PRODUCT_CATEGORY,
    premiumFeature,
    purchaseFlowId: PURCHASED_FLOW_ID,
    trial: true,
  };

  const plan = isGoldReinforcementEnabled
    ? { ...availablePackages.bronze, name: 'Personal' }
    : availablePackages.bronze;

  const handleSubmit = (paymentUrl) => {
    dispatch(
      yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    trackAsync(events.UPGRADE_INITIATED, {
      siteId,
      templateBuildSlug: getTemplateBuildSlugTrait(),
      ...traits,
    }).then(() => {
      saveSiteWithEffect(() => {
        redirectToUrl(paymentUrl);
      });
    });
  };

  const handleCancel = () => {
    if (actionId === actionIds.GET_FREE_DOMAIN) {
      trackEvent(events.UPGRADE_MAYBE_LATER_CLICKED, traits);

      dialogs.operations.hide();
      dispatch(publishing.actions.publishWebsite(domainName));
      return;
    }

    trackEvent(events.UPGRADE_EXPLORE_OTHER_PLANS_CLICKED, traits);

    onCancel();
  };

  useEffect(() => {
    trackEvent(events.UPGRADE_DISPLAYED, traits);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <AutomaticPlanSelector
      actionId={actionId}
      siteId={siteId}
      plan={plan}
      isB2C={isB2C}
      partnerId={partnerData.id}
      discountType={discountType}
      currency={currency}
      successUrl={successUrl}
      {...(actionId === actionIds.BUY_DOMAIN && { domainName })}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

AutomaticPlanSelectorContainer.propTypes = {
  domainName: PropTypes.string,
  actionId: PropTypes.string.isRequired,
  successUrl: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
};

export default AutomaticPlanSelectorContainer;
